import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import { bioLimits } from '../components/ProfileCompletionGate';
import AudioAvatarPlayer from '../components/AudioAvatarPlayer';
import AudioAvatarSection from '../components/AudioAvatarSection';
import { Button } from '../components/ui/button';
import { Settings, LogOut, Film, Volume2, Play, Wallet, ShieldCheck, Radio, Plus, Pencil, Share2, BookOpen } from 'lucide-react';
import EditPostDialog from '../components/EditPostDialog';
import VerifiedBadge from '../components/VerifiedBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/use-toast';
import { useProfileShare } from '../hooks/useProfileShare';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { fetchUserPosts, mediaUrl, deletePost } from '../api/posts';
import { livesByUser } from '../api/lives';
import LiveCard from '../components/LiveCard';
import CreateLiveDialog from '../components/CreateLiveDialog';
import VipCreatorPanel from '../components/VipCreatorPanel';
import FollowListDialog from '../components/FollowListDialog';
import RaccontiSection from '../components/vetrina/RaccontiSection';
import VoiceTabsBar from '../components/vetrina/VoiceTabsBar';
import CategoriesTab from '../components/vetrina/CategoriesTab';
import api from '../api/client';
import useAppConfig from '../hooks/useAppConfig';
import { LANGUAGES } from '../lib/creatorMeta';
import { useCategories } from '../api/categories';

const Stat = ({ value, label, onClick, testId }) => {
  const inner = (
    <>
      <span className="text-base font-semibold md:text-base">{(value || 0).toLocaleString()}</span>
      <span className="text-sm text-foreground">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-center hover:opacity-70 md:flex-row md:items-baseline md:gap-1.5"
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex flex-col items-center md:flex-row md:items-baseline md:gap-1.5" data-testid={testId}>
      {inner}
    </div>
  );
};

const PostThumb = ({ post, onClick }) => {
  const url = mediaUrl(post.media_url);
  return (
    <button
      onClick={onClick}
      className="group relative aspect-square overflow-hidden bg-secondary"
      data-testid={`profile-thumb-${post.id}`}
    >
      {post.media_type === 'image' && (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      )}
      {post.media_type === 'video' && (
        <>
          <video src={url} className="h-full w-full object-cover" muted preload="metadata" />
          <div className="absolute right-2 top-2 text-white drop-shadow">
            <Film className="h-4 w-4" />
          </div>
        </>
      )}
      {post.media_type === 'audio' && (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300">
          <Volume2 className="h-10 w-10 text-foreground" strokeWidth={1.5} />
        </div>
      )}
      {post.media_type === 'text' && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-neutral-200 to-neutral-300 px-2">
          <BookOpen className="h-8 w-8 text-foreground" strokeWidth={1.5} />
          {post.title && <span className="line-clamp-2 text-center text-[10px] font-semibold">{post.title}</span>}
        </div>
      )}
      <div className="absolute inset-0 hidden items-center justify-center bg-black/30 text-white group-hover:flex">
        <Play className="h-7 w-7" />
      </div>
    </button>
  );
};

const PostModal = ({ post, onClose, onDeleted, onUpdated, isOwner }) => {
  const { toast } = useToast();
  const { t } = useTranslation('profile');
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (!post) return null;

  const handleDelete = async () => {
    if (!window.confirm(t('modal.delete_confirm'))) return;
    setDeleting(true);
    try {
      await deletePost(post.id);
      toast({ title: t('modal.delete_success') });
      onDeleted?.(post.id);
      onClose();
    } catch (_e) {
      toast({ title: t('modal.delete_error'), variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const url = mediaUrl(post.media_url);

  return (
    <>
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl p-0">
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr]">
          <div className="flex aspect-square w-full items-center justify-center bg-black">
            {post.media_type === 'image' && (
              <img src={url} alt="post" className="max-h-full max-w-full object-contain" />
            )}
            {post.media_type === 'video' && (
              <video src={url} controls className="max-h-full max-w-full" autoPlay />
            )}
            {post.media_type === 'audio' && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-neutral-100 to-neutral-200 p-6">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-card shadow-md">
                  <Volume2 className="h-12 w-12 text-foreground" strokeWidth={1.6} />
                </div>
                <audio src={url} controls autoPlay className="w-full" />
              </div>
            )}
            {post.media_type === 'text' && (
              <div className="h-full w-full overflow-y-auto bg-card p-6 text-left">
                {post.title && <h2 className="mb-3 text-xl font-bold">{post.title}</h2>}
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{post.body_text}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col p-4">
            <DialogHeader>
              <DialogTitle className="text-base">
                {post.author?.username}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t('modal.aria_desc', { username: post.author?.username || '' })}
              </DialogDescription>
            </DialogHeader>
            {post.location && (
              <p className="mt-1 text-xs text-muted-foreground">{post.location}</p>
            )}
            {post.caption && (
              <p className="mt-3 whitespace-pre-wrap text-sm">{post.caption}</p>
            )}
            <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">
              {new Date(post.created_at).toLocaleString()}
            </p>
            <div className="mt-auto flex justify-end gap-1">
              {isOwner && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setEditOpen(true)}
                    data-testid="profile-post-edit-btn"
                  >
                    <Pencil className="mr-1.5 h-4 w-4" /> {t('modal.edit_btn')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="profile-post-delete-btn"
                  >
                    {deleting ? t('modal.deleting') : t('modal.delete_btn')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {isOwner && (
      <EditPostDialog
        post={post}
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdated={(p) => onUpdated?.(p)}
      />
    )}
    </>
  );
};

const ProfilePage = () => {
  const { user, logout, updateUser, refresh } = useAuth();
  const { shareProfile: handleShareProfile } = useProfileShare(user?.username);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('profile');
  const { peopleFeedEnabled } = useAppConfig();
  const { categories, nameOf } = useCategories();
  const isVoicePro = !!user?.is_verified_creator;
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    bio: user?.bio || '',
    website: user?.website || '',
    avatar_url: user?.avatar_url || '',
    languages: user?.languages || [],
    category_slug: user?.category_slug || '',
    secondary_category_slugs: user?.secondary_category_slugs || [],
    gender: user?.gender || '',
  });
  const [tab, setTab] = useState('audio');
  const [saving, setSaving] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [lives, setLives] = useState([]);
  const [loadingLives, setLoadingLives] = useState(true);
  const [createLiveOpen, setCreateLiveOpen] = useState(false);
  // NeoUI — followers/following dialog + username change.
  const [followDlg, setFollowDlg] = useState(null); // null | 'followers' | 'following'
  const [usernameField, setUsernameField] = useState(user?.username || '');
  const [usernameCheck, setUsernameCheck] = useState(null); // null|checking|available|taken|invalid

  useEffect(() => {
    const u = usernameField.trim().toLowerCase();
    if (!editOpen) return undefined;
    if (u === (user?.username || '')) { setUsernameCheck(null); return undefined; }
    if (u.length < 3 || !u.replace(/_/g, '').replace(/\./g, '').match(/^[a-z0-9]+$/)) {
      setUsernameCheck('invalid');
      return undefined;
    }
    setUsernameCheck('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/users/check/${encodeURIComponent(u)}`);
        setUsernameCheck(res.data.available ? 'available' : 'taken');
      } catch (_e) { setUsernameCheck(null); }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameField, editOpen]);

  const loadPosts = async () => {
    if (!user?.username) return;
    setLoadingPosts(true);
    try {
      const data = await fetchUserPosts(user.username);
      setPosts(data);
    } catch (_e) {
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const loadLives = async () => {
    if (!user?.username) return;
    setLoadingLives(true);
    try {
      const data = await livesByUser(user.username);
      setLives(data);
    } catch (_e) {
      setLives([]);
    } finally {
      setLoadingLives(false);
    }
  };

  useEffect(() => {
    loadPosts();
    loadLives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  if (!user) return null;

  const openEdit = () => {
    setForm({
      full_name: user.full_name || '',
      bio: user.bio || '',
      website: user.website || '',
      avatar_url: user.avatar_url || '',
      languages: user.languages || [],
      category_slug: user.category_slug || '',
      secondary_category_slugs: user.secondary_category_slugs || [],
      gender: user.gender || '',
    });
    setUsernameField(user.username || '');
    setUsernameCheck(null);
    setEditOpen(true);
  };

  // Phase 99 — deep-link from "Diventa Creator": /profile?edit=1&focus=audio-avatar
  // opens the edit dialog and scrolls straight to the Voice Bio section.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('edit') === '1' && user) {
      openEdit();
      if (params.get('focus') === 'audio-avatar') {
        setTimeout(() => {
          document.getElementById('audio-avatar-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 700);
      }
      navigate('/profile', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  const save = async () => {
    const bioTrim = (form.bio || '').trim();
    const { min: bioMin, max: bioMax } = bioLimits(user);
    if (bioTrim && bioTrim.length < bioMin) {
      toast({ title: 'Bio troppo corta', description: `La bio deve contenere almeno ${bioMin} caratteri.`, variant: 'destructive' });
      return;
    }
    if (bioTrim.length > bioMax) {
      toast({ title: 'Bio troppo lunga', description: `La bio può contenere al massimo ${bioMax} caratteri.`, variant: 'destructive' });
      return;
    }
    const newUsername = usernameField.trim().toLowerCase();
    if (newUsername !== user.username && usernameCheck === 'taken') {
      toast({ title: t('edit_dialog.username_taken'), variant: 'destructive' });
      return;
    }
    if (isVoicePro && !(form.category_slug || '').trim()) {
      toast({ title: 'Categoria obbligatoria', description: 'Scegli la categoria di cui ami parlare: appare sulla tua card nel feed Persone.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      // NeoUI — username change first (dedicated endpoint w/ 14-day cooldown).
      if (newUsername && newUsername !== user.username) {
        try {
          await api.post('/auth/me/username', { username: newUsername });
        } catch (e) {
          const detail = String(e.response?.data?.detail || '').split('|').pop();
          toast({ title: t('edit_dialog.username_error'), description: detail || '', variant: 'destructive' });
          setSaving(false);
          return;
        }
      }
      await updateUser(isVoicePro ? form : { ...form, secondary_category_slugs: undefined });
      await refresh();
      toast({ title: t('edit_dialog.success') });
      setEditOpen(false);
    } catch (e) {
      const detail = String(e.response?.data?.detail || '').split('|').pop();
      toast({ title: t('edit_dialog.error'), description: detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleted = async (id) => {
    setPosts((ps) => ps.filter((p) => p.id !== id));
    await refresh();
  };

  return (
    <div className="mx-auto max-w-[935px] px-4 pt-6 md:pt-10">
      <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:gap-12">
        <div className="flex w-full justify-center md:w-auto">
          <div className="relative">
            <Avatar src={user.avatar_url} alt={user.username} size={150} />
            {user.audio_avatar_url && (
              <AudioAvatarPlayer src={user.audio_avatar_url} size="md" className="absolute bottom-1 right-1" />
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="flex items-center gap-1.5 text-xl font-light">
              {user.username}
              {user.is_verified_creator && <VerifiedBadge size={18} />}
            </h1>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={openEdit}
                variant="secondary"
                className="h-8 rounded-md bg-secondary px-4 text-sm font-semibold text-foreground hover:bg-muted"
              >
                {t('actions.edit_profile')}
              </Button>
              <Button
                variant="secondary"
                className="h-8 rounded-md bg-secondary px-4 text-sm font-semibold text-foreground hover:bg-muted"
              >
                {t('actions.view_archive')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate(user.is_verified_creator ? '/lives?tab=earnings' : '/wallet')}
                className="h-8 rounded-md bg-secondary px-4 text-sm font-semibold text-foreground hover:bg-muted"
                data-testid="profile-wallet-btn"
              >
                <Wallet className="mr-1.5 h-4 w-4" />
                {user.is_verified_creator ? t('actions.creator_dashboard') : t('actions.wallet')}
              </Button>
              <Button
                variant="secondary"
                onClick={handleShareProfile}
                className="h-8 rounded-md bg-secondary px-4 text-sm font-semibold text-foreground hover:bg-muted"
                data-testid="share-profile-btn"
              >
                <Share2 className="mr-1.5 h-4 w-4" />
                {t('actions.share_profile')}
              </Button>
              {user.is_admin && (
                <Button
                  variant="secondary"
                  onClick={() => navigate('/admin')}
                  className="h-8 rounded-md bg-violet-100 px-4 text-sm font-semibold text-violet-700 hover:bg-violet-200"
                >
                  <ShieldCheck className="mr-1.5 h-4 w-4" /> {t('actions.admin')}
                </Button>
              )}
              <button
                onClick={() => navigate('/settings')}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-secondary"
                aria-label={t('actions.settings_aria')}
                data-testid="profile-settings-btn"
              >
                <Settings className="h-5 w-5" strokeWidth={1.8} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-8">
            {peopleFeedEnabled ? (
              /* Voice-First — count Racconti (audio+testo), no legacy post count */
              <Stat
                value={posts.filter((p) => p.media_type === 'audio' || p.media_type === 'text').length}
                label="Racconti"
                testId="profile-stat-racconti"
              />
            ) : (
              <Stat value={user.posts_count} label={t('stats.posts')} testId="profile-stat-posts" />
            )}
            {!peopleFeedEnabled && (
              <>
                {/* Phase F9 — social counters hidden in the Voice-First
                    experience (people_feed flag = source of truth, reversible). */}
                {!peopleFeedEnabled && (
                  <>
                    <Stat value={user.followers_count} label={t('stats.followers')} onClick={() => setFollowDlg('followers')} testId="profile-stat-followers" />
                    <Stat value={user.following_count} label={t('stats.following')} onClick={() => setFollowDlg('following')} testId="profile-stat-following" />
                  </>
                )}
              </>
            )}
          </div>
          <div className="text-sm">
            <p className="font-semibold">{user.full_name}</p>
            {user.bio && <p className="whitespace-pre-wrap">{user.bio}</p>}
            {user.website && (
              <a
                href={user.website.startsWith('http') ? user.website : `https://${user.website}`}
                target="_blank"
                rel="noreferrer"
                className="font-semibold gold-text hover:underline"
              >
                {user.website}
              </a>
            )}
          </div>
        </div>
      </div>

      <VipCreatorPanel />

      <div className="mt-10 border-t border-border pt-4">
        {/* Voice-First profile menu: 🎙 | 📖 | 🔴 | 🏷 (legacy POST /
            SALVATI / TAGGATI removed from the UI — data untouched) */}
        <VoiceTabsBar active={tab} onChange={setTab} testPrefix="profile-tab" />

        {tab === 'audio' && (
          <div className="pb-16" data-testid="profile-audio-panel">
            {loadingPosts ? (
              <div className="flex justify-center py-14">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
              </div>
            ) : (
              <RaccontiSection
                posts={posts}
                types={['audio']}
                title={null}
                username={user.username}
                onOpen={(p) => setSelectedPost(p)}
                emptyText="Non hai ancora pubblicato racconti audio. Vai su Crea per registrare il primo."
                testId="profile-racconti-audio"
              />
            )}
          </div>
        )}

        {tab === 'text' && (
          <div className="pb-16" data-testid="profile-text-panel">
            {loadingPosts ? (
              <div className="flex justify-center py-14">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
              </div>
            ) : (
              <RaccontiSection
                posts={posts}
                types={['text']}
                title={null}
                username={user.username}
                onOpen={(p) => setSelectedPost(p)}
                emptyText="Non hai ancora pubblicato racconti testuali. Vai su Crea per scrivere il primo."
                testId="profile-racconti-text"
              />
            )}
          </div>
        )}

        {tab === 'live' && (
          <div className="pb-16 pt-4" data-testid="profile-lives-tab">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('lives_tab.count_one', { count: lives.length, defaultValue_plural: t('lives_tab.count_other', { count: lives.length }) })}
              </p>
              <Button
                onClick={() => setCreateLiveOpen(true)}
                className="h-9 bg-red-500 hover:bg-red-600"
                data-testid="schedule-live-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" /> {t('lives_tab.schedule_btn')}
              </Button>
            </div>
            {loadingLives ? (
              <div className="flex justify-center py-10">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
              </div>
            ) : lives.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-black">
                  <Radio className="h-8 w-8 text-red-500" strokeWidth={1.6} />
                </div>
                <h3 className="text-2xl font-bold">{t('lives_tab.empty_title')}</h3>
                <p className="mt-2 text-sm text-foreground">
                  {t('lives_tab.empty_desc')}
                </p>
                <button
                  onClick={() => setCreateLiveOpen(true)}
                  className="mt-4 text-sm font-semibold gold-text hover:underline"
                >
                  {t('lives_tab.empty_cta')}
                </button>
              </div>
            ) : (() => {
              const upcoming = lives.filter((l) => l.status === 'scheduled' || l.status === 'live');
              const past = lives.filter((l) => l.status === 'ended');
              return (
                <div className="space-y-6">
                  {upcoming.length > 0 && (
                    <section data-testid="profile-lives-upcoming">
                      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider gold-text">
                        <Radio className="h-3.5 w-3.5" /> {t('lives_tab.upcoming_title')}
                      </h3>
                      <div className="flex flex-col gap-2">
                        {upcoming.map((l) => <LiveCard key={l.id} live={l} />)}
                      </div>
                    </section>
                  )}
                  {past.length > 0 && (
                    <section data-testid="profile-lives-past">
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('lives_tab.past_title', { count: past.length })}
                      </h3>
                      <div className="flex flex-col gap-2 opacity-90">
                        {past.map((l) => <LiveCard key={l.id} live={l} />)}
                      </div>
                    </section>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {tab === 'categories' && (
          <div className="pb-16 pt-4" data-testid="profile-categories-panel">
            <CategoriesTab
              username={user.username}
              categorySlug={user.category_slug}
              categoryName={user.category_slug ? nameOf(user.category_slug) : ''}
              secondaryCategories={(user.secondary_category_slugs || []).map((s) => ({ slug: s, name: nameOf(s) })).filter((c) => c.name)}
              topics={user.topics || []}
              lives={lives}
            />
          </div>
        )}
      </div>

      <div className="mt-2 flex justify-center pb-24">
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="h-9 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut className="mr-2 h-4 w-4" /> {t('actions.logout')}
        </Button>
      </div>

      <PostModal
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        onDeleted={handleDeleted}
        onUpdated={(p) => {
          setSelectedPost(p);
          setPosts((prev) => prev.map((x) => (x.id === p.id ? p : x)));
        }}
        isOwner={selectedPost?.user_id === user.id || selectedPost?.author?.id === user.id}
      />

      <CreateLiveDialog
        open={createLiveOpen}
        onOpenChange={setCreateLiveOpen}
        onCreated={() => loadLives()}
      />

      {/* NeoUI — followers / following lists */}
      <FollowListDialog
        open={!!followDlg}
        onOpenChange={(o) => !o && setFollowDlg(null)}
        username={user.username}
        type={followDlg || 'followers'}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('edit_dialog.title')}</DialogTitle>
            <DialogDescription>
              {t('edit_dialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex items-center gap-3">
              <Avatar src={form.avatar_url} alt={user.username} size={56} />
              <div className="flex-1">
                <p className="text-sm font-semibold">{user.username}</p>
                <p className="text-xs text-muted-foreground">{t('edit_dialog.avatar_upload_hint')}</p>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('edit_dialog.avatar_label')}</label>
              <input
                type="file"
                accept="image/*"
                data-testid="avatar-file-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const { uploadAvatar } = await import('../api/account');
                    const updated = await uploadAvatar(file);
                    setForm({ ...form, avatar_url: updated.avatar_url });
                    toast({ title: t('edit_dialog.avatar_success') });
                    await refresh();
                  } catch (err) {
                    toast({
                      title: t('edit_dialog.avatar_error_title'),
                      description: err.response?.data?.detail || t('edit_dialog.avatar_error_default'),
                      variant: 'destructive',
                    });
                  } finally {
                    e.target.value = '';
                  }
                }}
                className="block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-amber-500/20 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:gold-text hover:file:bg-amber-500/30"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t('edit_dialog.avatar_hint')}</p>
            </div>
            <AudioAvatarSection />
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('edit_dialog.username_label')}</label>
              <Input
                value={usernameField}
                onChange={(e) => setUsernameField(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                maxLength={30}
                data-testid="edit-username-input"
              />
              {usernameField.trim().toLowerCase() !== user.username && (
                <p className={`mt-1 text-xs ${
                  usernameCheck === 'available' ? 'text-emerald-500'
                    : usernameCheck === 'taken' || usernameCheck === 'invalid' ? 'text-red-500'
                      : 'text-muted-foreground'
                }`} data-testid="username-check-status">
                  {usernameCheck === 'checking' && t('edit_dialog.username_checking')}
                  {usernameCheck === 'available' && t('edit_dialog.username_available')}
                  {usernameCheck === 'taken' && t('edit_dialog.username_taken')}
                  {usernameCheck === 'invalid' && t('edit_dialog.username_invalid')}
                </p>
              )}
              <p className="mt-1 text-[11px] text-muted-foreground">{t('edit_dialog.username_hint')}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('edit_dialog.name_label')}</label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('edit_dialog.bio_label')}</label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                maxLength={bioLimits(user).max}
                rows={isVoicePro ? 4 : 3}
                placeholder={isVoicePro ? undefined : 'Es. Amo viaggiare, conoscere persone e parlare di cucina, vita e nuovi posti.'}
                data-testid="edit-bio-textarea"
              />
              <p className={`mt-1 text-right text-xs ${form.bio.trim() && form.bio.trim().length < bioLimits(user).min ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="edit-bio-counter">
                {form.bio.length}/{bioLimits(user).max} · minimo {bioLimits(user).min} caratteri
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">Lingue parlate</label>
              <div className="flex flex-wrap gap-2" data-testid="edit-languages-picker">
                {LANGUAGES.map((l) => {
                  const active = (form.languages || []).includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        languages: active
                          ? form.languages.filter((c) => c !== l.code)
                          : [...(form.languages || []), l.code],
                      })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                        active ? 'border-amber-500 bg-amber-500/15 gold-text' : 'border-border text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid={`edit-lang-${l.code}`}
                    >
                      {l.flag} {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{isVoicePro ? 'Categoria principale *' : 'Categoria'}</label>
              <select
                value={form.category_slug}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({
                    ...form,
                    category_slug: v,
                    secondary_category_slugs: v ? (form.secondary_category_slugs || []).filter((s) => s !== v) : [],
                  });
                }}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="edit-category-select"
              >
                <option value="">Nessuna categoria</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {isVoicePro
                  ? 'La tua categoria principale — aiuta le persone a trovarti nel feed.'
                  : 'Una sola categoria, facoltativa — aiuta le persone a trovarti nel feed.'}
              </p>
            </div>
            {isVoicePro && (
              <div data-testid="edit-secondary-categories">
                <label className="mb-1 block text-xs font-semibold text-foreground">Categorie secondarie (max 2)</label>
                {[0, 1].map((i) => {
                  const current = (form.secondary_category_slugs || [])[i] || '';
                  const other = (form.secondary_category_slugs || [])[i === 0 ? 1 : 0] || '';
                  const setSecondary = (v) => {
                    const next = [...(form.secondary_category_slugs || [])];
                    next[i] = v;
                    const compact = [];
                    next.forEach((s) => {
                      if (s && s !== form.category_slug && !compact.includes(s)) compact.push(s);
                    });
                    setForm({ ...form, secondary_category_slugs: compact.slice(0, 2) });
                  };
                  return (
                    <select
                      key={i}
                      value={current}
                      onChange={(e) => setSecondary(e.target.value)}
                      disabled={!form.category_slug}
                      className="mb-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
                      data-testid={`edit-secondary-category-select-${i + 1}`}
                    >
                      <option value="">Nessuna</option>
                      {categories
                        .filter((c) => c.slug !== form.category_slug && c.slug !== other)
                        .map((c) => (
                          <option key={c.slug} value={c.slug}>{c.name}</option>
                        ))}
                    </select>
                  );
                })}
                <p className="text-[11px] text-muted-foreground">
                  Solo Voce Pro: fino a 2 categorie in più oltre alla principale. {!form.category_slug && 'Scegli prima la categoria principale.'}
                </p>
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">Genere</label>
              <div className="flex flex-wrap gap-2" data-testid="edit-gender-picker">
                {[
                  { v: 'male', label: 'Uomo' },
                  { v: 'female', label: 'Donna' },
                  { v: 'other', label: 'Altro' },
                ].map((g) => (
                  <button
                    key={g.v || 'none'}
                    type="button"
                    onClick={() => setForm({ ...form, gender: g.v })}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                      (form.gender || '') === g.v ? 'border-amber-500 bg-amber-500/15 gold-text' : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`edit-gender-${g.v || 'none'}`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Mai dedotto automaticamente: puoi cambiarlo quando vuoi.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('edit_dialog.website_label')}</label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder={t('edit_dialog.website_placeholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>
              {t('edit_dialog.cancel')}
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="gold-gradient"
            >
              {saving ? t('edit_dialog.saving') : t('edit_dialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
